import { Adapter } from './adapter';
import { LocalDataService } from 'src/packages/mitsBasics';
import {
  BookingContext,
  BookingPosition,
  BookingServices,
  BookingTransactionId,
  TransactionQueuedBooking,
} from '../../types';
import { ModelAdapters } from '../model-adapters';
import { RandomUtils } from '../../util/random-utils';

interface TransactionQueue {
  // Nur eine Queue pro id
  id: number;
  // Einzelne Buchungen, z.B. Bufferstock (-5) → Maschine (+5)
  bookings: TransactionQueuedBooking[];
}

const LOCAL_STORAGE_KEY = 'bookingTransactionQueue';

/**
 * Verwaltet die lokale Rollback-Queue je Transaction: Buchungen anhängen,
 * abfragen, gefiltert entfernen und die Queue leeren.
 */
export class TransactionAdapter extends Adapter {
  private readonly transactionQueueService: LocalDataService<TransactionQueue>;

  constructor(services: BookingServices, adapters: ModelAdapters) {
    super(services, adapters);
    this.transactionQueueService = new LocalDataService(LOCAL_STORAGE_KEY);
  }

  /**
   * Hängt eine Buchung an die Rollback-Queue der Transaction (legt sie bei
   * Bedarf an).
   *
   * @param id Transaction-ID
   * @param position Gebuchte Position
   * @param booking Buchungskontext
   * @returns `false`, wenn das Speichern fehlschlägt.
   */
  public enqueueBooking(
    id: BookingTransactionId,
    position: BookingPosition,
    booking: BookingContext,
  ): boolean {
    const queuedBooking: TransactionQueuedBooking = {
      entryId: RandomUtils.uuid(),
      position,
      booking
    };
    // Queue-Objekt im LocalStorage anlegen, falls noch nicht vorhanden
    const queue: TransactionQueue = this.transactionQueueService.find(id)
      ?.content ?? { id, bookings: [] };
    queue.bookings.push(queuedBooking);
    return !!this.transactionQueueService.save(queue);
  }

  /**
   * Liefert die gequeuten Buchungen einer Transaction.
   *
   * @param id Transaction-ID
   * @param filter Optionales Prädikat zum Einschränken
   * @returns Passende Buchungen, leer wenn keine Queue existiert.
   */
  public findBookings(id: BookingTransactionId, filter?: (qb: TransactionQueuedBooking) => boolean): TransactionQueuedBooking[] {
    const bookings =  this.transactionQueueService.find(id)?.content?.bookings ?? [];
    return filter ? bookings.filter(filter) : bookings;
  }

  /**
   * Entfernt Buchungen aus der Queue (ohne Filter: alle).
   *
   * @param id Transaction-ID
   * @param filter Optionales Prädikat; nur passende werden entfernt
   * @returns `false`, wenn keine Queue für die ID existiert.
   */
  public removeBookings(id: BookingTransactionId, filter?: (qb: TransactionQueuedBooking) => boolean): boolean {
    const data = this.transactionQueueService.find(id);
    if (!data) {
      return false;
    }
    const queue = data.content;
    if (filter) {
      // Wir wollen alle Bookings behalten, die nicht im Filter enthalten sind
      queue.bookings = [...queue.bookings.filter(qb => !filter(qb))];
    } else {
      // Ansonsten alle Bookings löschen
      queue.bookings = [];
    }
    return !!this.transactionQueueService.save(queue);
  }

  /**
   * Löscht die komplette Rollback-Queue einer Transaction. Idempotent.
   *
   * @param id Transaction-ID
   * @returns `true`, auch wenn keine Queue existierte.
   */
  public clear(id: BookingTransactionId): boolean {
    if (!this.transactionQueueService.find(id)) {
      return true;
    }
    return this.transactionQueueService.delete(id);
  }
}
